/**
 * Markdown → 口播文本 + 分句（学习板块 TTS「听书」，纯前端、无依赖）
 * 目标：把章节 Markdown 转成适合朗读的连续中文文本，并切成适合逐句播放/进度定位的短句队列。
 * 规则（与实施方案一致）：
 *  - 标题/加粗/斜体/删除线/行内码/引用/列表 标记：剥离符号，保留文字
 *  - 链接：保留锚文本，丢弃 URL；图片：整体跳过（不朗读文件名/URL）
 *  - 代码块 / mermaid：跳过，原位占位「（代码示例，略）」
 *  - 表格：跳过，原位占位「（此处为表格，请看屏幕）」
 *  - 数学（$..$ / $$..$$ / \(..\) / \[..\]）、裸 HTML 标签：跳过
 * 分句：按中文句末标点断句，弱标点（，、：）按长度兜底，单块上限 maxLen，避免朗读引擎单条过长。
 * 注：不使用正则 lookbehind（低版本 Android WebView 会抛异常）。
 */

const TABLE_HINT = '（此处为表格，请看屏幕）';
const CODE_HINT = '（代码示例，略）';

/** 是否像一行表格（含 ≥1 个竖线分隔） */
function looksLikeTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') || (t.includes('|') && t.includes('-'));
}

/** 剥离行内标记，返回可朗读文本 */
function stripInline(input: string): string {
  let s = input;
  // 图片 ![alt](url) → 丢（连 alt 一起，避免读出无意义 alt）
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  // 引用式图片 ![[..]]
  s = s.replace(/!\[[^\]]*\]\[[^\]]*\]/g, '');
  // 链接 [text](url) / [text][ref] → text
  s = s.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  s = s.replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1');
  // 数学块内联
  s = s.replace(/\$\$[^$]*\$\$/g, '');
  s = s.replace(/\$[^$\n]*\$/g, '');
  s = s.replace(/\\\([\s\S]*?\\\)/g, '');
  s = s.replace(/\\\[[\s\S]*?\\\]/g, '');
  // 纯数字引用标记 [1] / [^1]
  s = s.replace(/\[\^?\d+\]/g, '');
  // 加粗/斜体/删除线/行内码（保留内容）
  s = s.replace(/(\*\*|__)(.*?)\1/g, '$2');
  s = s.replace(/(\*|_)(.*?)\1/g, '$2');
  s = s.replace(/~~(.*?)~~/g, '$1');
  s = s.replace(/`([^`]*)`/g, '$1');
  // HTML 标签
  s = s.replace(/<[^>]+>/g, '');
  // 残留 markdown 强调星号/下划线散落
  s = s.replace(/[*_~]/g, '');
  return s.trim();
}

/** Markdown → 连续口播文本 */
export function mdToSpeechText(md: string): string {
  const out: string[] = [];
  const lines = (md || '').replace(/\r\n?/g, '\n').split('\n');
  let inFence = false;
  let fenceHinted = false;
  let inTable = false;

  for (const raw of lines) {
    const line = raw.trimEnd();
    const fence = /^\s*(```|~~~)/.test(line);
    if (fence) {
      if (!inFence) {
        inFence = true;
        fenceHinted = false;
      } else {
        inFence = false;
      }
      inTable = false;
      continue;
    }
    if (inFence) {
      if (!fenceHinted) {
        out.push(CODE_HINT);
        fenceHinted = true;
      }
      continue;
    }

    // 表格连续段：只在进入时插一次占位
    if (looksLikeTableRow(line)) {
      if (!inTable) {
        out.push(TABLE_HINT);
        inTable = true;
      }
      continue;
    }
    inTable = false;

    if (!line.trim()) {
      out.push(''); // 段落分隔
      continue;
    }

    let text = line;
    // 标题：去 #
    text = text.replace(/^\s*#{1,6}\s*/, '');
    // 引用：去前导 >
    text = text.replace(/^\s*>+\s*/, '');
    // 列表符号：去 - * + 与有序 1. / 1)
    text = text.replace(/^\s*[-*+]\s+/, '');
    text = text.replace(/^\s*\d+[.)]\s+/, '');
    // 水平分割线：跳过
    if (/^\s*([-*_])\1{1,}\s*$/.test(text)) continue;
    // 残留 # 符号
    text = text.replace(/#+/g, '');

    const cleaned = stripInline(text);
    if (cleaned) out.push(cleaned);
  }

  // 合并：段落间以换行分隔，去多余空行
  return out
    .join('\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** 按句末标点切句（不依赖 lookbehind）；返回句子数组（含尾标点） */
function splitByTerminator(text: string): string[] {
  const sentences: string[] = [];
  let buf = '';
  for (const ch of text) {
    buf += ch;
    if ('。！？!?；;'.includes(ch)) {
      const t = buf.trim();
      if (t) sentences.push(t);
      buf = '';
    }
  }
  const tail = buf.trim();
  if (tail) sentences.push(tail);
  return sentences;
}

/** 弱标点兜底切分（单句仍过长时） */
function splitByWeak(text: string): string[] {
  const parts: string[] = [];
  let buf = '';
  for (const ch of text) {
    buf += ch;
    if ('，、：:,. '.includes(ch) && buf.trim().length >= 24) {
      const t = buf.trim();
      if (t) parts.push(t);
      buf = '';
    }
  }
  const tail = buf.trim();
  if (tail) parts.push(tail);
  return parts;
}

/**
 * 口播文本 → 短句队列：句末断句 + 按 maxLen 合并相邻句；超限句再弱标点/硬切兜底。
 */
export function splitSpeechChunks(md: string, maxLen = 120): string[] {
  const text = mdToSpeechText(md);
  if (!text) return [];
  const chunks: string[] = [];
  let cur = '';

  const flush = () => {
    const t = cur.trim();
    if (t) chunks.push(t);
    cur = '';
  };

  for (const para of text.split('\n')) {
    const p = para.trim();
    if (!p) {
      flush();
      continue;
    }
    // 占位句单独成块，避免和正文粘一起
    if (p === TABLE_HINT || p === CODE_HINT) {
      flush();
      chunks.push(p);
      continue;
    }
    const sentences = splitByTerminator(p);
    for (let s of sentences) {
      // 单句仍超限：弱切 + 硬切
      while (s.length > maxLen) {
        const weak = splitByWeak(s);
        let piece = weak.shift() ?? s.slice(0, maxLen);
        if (piece.length > maxLen) piece = s.slice(0, maxLen);
        if (cur && (cur + piece).length <= maxLen) {
          cur += piece;
        } else {
          flush();
          cur = piece;
        }
        flush();
        s = s.slice(piece.length).trim();
        if (!s) break;
      }
      if (!s) continue;
      if (cur && (cur + s).length <= maxLen) {
        cur += s;
      } else {
        flush();
        cur = s;
      }
    }
    flush();
  }
  flush();
  return chunks;
}
